import * as React from 'react';

import { cn } from '../../utils/cn';

const CLOSE_THRESHOLD_RATIO = 0.25;
const MIN_CLOSE_DISTANCE_PX = 80;
const SNAP_BACK_TRANSITION =
    'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s cubic-bezier(0.32, 0.72, 0, 1)';

/** Returns true when a downward swipe should dismiss the drawer. */
export function shouldDismissDrawerSwipe(
    deltaY: number,
    drawerHeight: number,
): boolean {
    if (deltaY <= 0) {
        return false;
    }

    return (
        deltaY >= MIN_CLOSE_DISTANCE_PX ||
        deltaY >= drawerHeight * CLOSE_THRESHOLD_RATIO
    );
}

const findDrawerElements = (from: EventTarget | null) => {
    if (!(from instanceof Element)) {
        return { drawer: null, overlay: null };
    }

    const drawer = from.closest('[data-vaul-drawer]');
    const portal = from.closest('[data-slot="drawer-portal"]');
    const overlay =
        portal?.querySelector('[data-vaul-overlay]') ??
        document.querySelector('[data-vaul-overlay]');

    return {
        drawer: drawer instanceof HTMLElement ? drawer : null,
        overlay: overlay instanceof HTMLElement ? overlay : null,
    };
};

interface DrawerSwipeZoneProps extends React.ComponentProps<'div'> {
    onDismiss: () => void;
    /**
     * Consulted once the swipe has passed the dismiss threshold. Returning
     * false snaps the drawer back instead — this zone drives the transform
     * itself, so a caller that simply ignores `onDismiss` would leave the
     * drawer parked wherever the drag ended.
     */
    canDismiss?: () => boolean;
}

/**
 * Touch swipe zone for bottom drawers on real mobile devices. Drives translate
 * and dismiss directly because vaul's handleOnly drag can miss move updates on
 * iOS even when pointer events are captured on the handle.
 */
export function DrawerSwipeZone({
    onDismiss,
    canDismiss,
    className,
    children,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    ...props
}: DrawerSwipeZoneProps) {
    const startYRef = React.useRef(0);
    const draggingRef = React.useRef(false);
    const drawerRef = React.useRef<HTMLElement | null>(null);
    const overlayRef = React.useRef<HTMLElement | null>(null);

    const resetDrawerPosition = React.useCallback(() => {
        const drawer = drawerRef.current;
        const overlay = overlayRef.current;
        if (!drawer) {
            return;
        }

        drawer.style.transition = SNAP_BACK_TRANSITION;
        drawer.style.transform = 'translate3d(0, 0, 0)';

        if (overlay) {
            overlay.style.transition = SNAP_BACK_TRANSITION;
            overlay.style.opacity = '1';
        }
    }, []);

    const finishSwipe = React.useCallback(
        (clientY: number) => {
            const drawer = drawerRef.current;
            if (!drawer || !draggingRef.current) {
                return;
            }

            draggingRef.current = false;

            const deltaY = clientY - startYRef.current;
            const drawerHeight = drawer.getBoundingClientRect().height;

            if (
                shouldDismissDrawerSwipe(deltaY, drawerHeight) &&
                (canDismiss?.() ?? true)
            ) {
                onDismiss();
                return;
            }

            resetDrawerPosition();
        },
        [onDismiss, canDismiss, resetDrawerPosition],
    );

    return (
        <div
            className={cn(
                'mx-auto mt-0.5 flex min-h-11 w-full shrink-0 touch-none items-center justify-center group-data-[vaul-drawer-direction=bottom]/drawer-content:flex',
                className,
            )}
            onPointerDown={(event) => {
                onPointerDown?.(event);

                const { drawer, overlay } = findDrawerElements(
                    event.currentTarget,
                );
                if (!drawer) {
                    return;
                }

                drawerRef.current = drawer;
                overlayRef.current = overlay;
                startYRef.current = event.clientY;
                draggingRef.current = true;

                event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event) => {
                onPointerMove?.(event);

                const drawer = drawerRef.current;
                if (!drawer || !draggingRef.current) {
                    return;
                }

                const deltaY = Math.max(0, event.clientY - startYRef.current);
                const drawerHeight = drawer.getBoundingClientRect().height;
                const progress = Math.min(deltaY / drawerHeight, 1);

                drawer.style.transition = 'none';
                drawer.style.transform = `translate3d(0, ${deltaY}px, 0)`;

                const overlay = overlayRef.current;
                if (overlay) {
                    overlay.style.transition = 'none';
                    overlay.style.opacity = String(1 - progress);
                }
            }}
            onPointerUp={(event) => {
                onPointerUp?.(event);
                finishSwipe(event.clientY);
            }}
            onPointerCancel={(event) => {
                onPointerCancel?.(event);
                finishSwipe(event.clientY);
            }}
            {...props}
        >
            {children ?? (
                <span
                    className="block h-1 w-9.5 rounded-full bg-gray-600/30"
                    aria-hidden
                />
            )}
        </div>
    );
}
